/**
 * useWorkspace Hook
 * Manages workspace data and operations with loading states and error handling
 */

import { useState, useEffect, useCallback } from 'react'
import { workspaceService, type Workspace, type UpdateWorkspaceData } from '../services/workspace-service'

/**
 * Hook state interface
 */
interface UseWorkspaceState {
  workspace: Workspace | null
  isLoading: boolean
  isUpdating: boolean
  error: string | null
  lastFetch: number | null
}

/**
 * Hook return interface
 */
interface UseWorkspaceReturn extends UseWorkspaceState {
  fetchWorkspace: () => Promise<void>
  updateWorkspace: (data: UpdateWorkspaceData) => Promise<void>
  clearError: () => void
  refresh: () => Promise<void>
}

/**
 * Custom hook for workspace management
 * Provides workspace data fetching, updating, and state management
 * 
 * @param autoFetch - Whether to automatically fetch workspace data on mount (default: true)
 * Custom hook for workspace management.
 * Provides workspace data fetching, updating, and state management.
 *
 * @param autoFetch - Whether to automatically fetch workspace data on mount (default: true)
 * @returns {object} An object containing workspace data, loading states, error handling, and operations:
 *   @property {Workspace|null} workspace - The current workspace data, or null if not loaded.
 *   @property {boolean} isLoading - True if workspace data is currently being fetched.
 *   @property {boolean} isUpdating - True if workspace data is currently being updated.
 *   @property {string|null} error - Error message if an error occurred, otherwise null.
 *   @property {number|null} lastFetch - Timestamp (ms) of the last successful fetch, or null.
 *   @method fetchWorkspace - Fetches workspace data from the API.
 *   @method updateWorkspace - Updates workspace data with the provided payload.
 *   @method clearError - Clears the current error state.
 *   @method refresh - Re-fetches workspace data from the API.
 */
export function useWorkspace(autoFetch: boolean = true): UseWorkspaceReturn {
  const [state, setState] = useState<UseWorkspaceState>({
    workspace: null,
    isLoading: false,
    isUpdating: false,
    error: null,
    lastFetch: null
  })

  /**
   * Clears the current error state
   */
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }))
  }, [])

  /**
   * Fetches workspace data from the API
   */
  const fetchWorkspace = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }))
      
      const workspace = await workspaceService.getWorkspace()
      
      setState(prev => ({
        ...prev,
        workspace,
        isLoading: false,
        lastFetch: Date.now(),
        error: null
      }))
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch workspace'
      
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }))
    }
  }, [])

  /**
   * Updates workspace data
   */
  const updateWorkspace = useCallback(async (updateData: UpdateWorkspaceData) => {
    try {
      setState(prev => ({ ...prev, isUpdating: true, error: null }))
      
      const updatedWorkspace = await workspaceService.updateWorkspace(updateData)
      
      setState(prev => ({
        ...prev,
        workspace: updatedWorkspace,
        isUpdating: false,
        error: null
      }))
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update workspace'
      
      setState(prev => ({
        ...prev,
        isUpdating: false,
        error: errorMessage
      }))
      
      // Re-throw the error so the component can handle it
      throw error
    }
  }, [])

  /**
   * Refreshes workspace data (same as fetchWorkspace but with different semantics)
   */
  const refresh = useCallback(async () => {
    await fetchWorkspace()
  }, [fetchWorkspace])

  // Auto-fetch workspace data on mount if enabled
  useEffect(() => {
    if (autoFetch) {
      fetchWorkspace()
    }
  }, [autoFetch, fetchWorkspace])

  return {
    // State
    workspace: state.workspace,
    isLoading: state.isLoading,
    isUpdating: state.isUpdating,
    error: state.error,
    lastFetch: state.lastFetch,
    
    // Actions
    fetchWorkspace,
    updateWorkspace,
    clearError,
    refresh
  }
}

/**
 * Hook variant that doesn't auto-fetch data
 * Use this when you want to manually control when data is fetched
 */

/**
 * Utility function to format workspace last updated time
 */
export function formatWorkspaceUpdateTime(workspace: Workspace | null): string {
  if (!workspace?.updatedAt) {
    return 'Never updated'
  }
  
  try {
    const date = new Date(workspace.updatedAt)
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date)
  } catch {
    return 'Invalid date'
  }
}

/**
 * Utility function to check if workspace data is stale
 */